import { useLocation } from 'react-router-dom'
import type { TodayAttendance } from '@/api/attendance'
import { useTodayAttendance } from './useTodayAttendance'

export const ATTENDANCE_RESTRICTED_HOME = '/my/library'
export const TRIAL_ATTENDANCE_RESTRICTED_HOME = '/my/profile'

const ATTENDANCE_BLOCKED_PREFIXES = [
  '/my/today',
  '/my/learn',
  '/my/special-lecture',
  '/my/test',
  '/my/diagnostic',
  '/aiq-test',
] as const

export interface UseAttendanceAccessOptions {
  enabled?: boolean
}

export interface AttendanceAccessResult {
  isLoading: boolean
  isRestricted: boolean
  redirect: string | null
}

export function resolveAttendanceRestrictedHome(
  attendance: Pick<TodayAttendance, 'trialStatus'>,
) {
  return attendance.trialStatus === 'PENDING' || attendance.trialStatus === 'ACTIVE'
    ? TRIAL_ATTENDANCE_RESTRICTED_HOME
    : ATTENDANCE_RESTRICTED_HOME
}

/**
 * 재원 원생의 당일 학습 접근 게이트.
 *
 * 등원 중(IN)일 때만 오늘 수업과 그 하위 학습 플로우를 허용한다.
 * 하원(OUT) 및 출결 미처리(null)는 도서관으로 보내며, 조회 실패는 fail-open 한다.
 */
export function useAttendanceAccess(
  options: UseAttendanceAccessOptions = {},
): AttendanceAccessResult {
  const { enabled = true } = options
  const path = useLocation().pathname
  const { data, isLoading, isError } = useTodayAttendance({ enabled })

  if (!enabled || isError || !data) {
    return { isLoading: enabled && isLoading, isRestricted: false, redirect: null }
  }

  const isRestricted =
    data.enrollmentStatus === 'ENROLLED' && data.attendanceStatus !== 'IN'
  const isBlockedPath = ATTENDANCE_BLOCKED_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  )

  return {
    isLoading,
    isRestricted,
    redirect: isRestricted && isBlockedPath
      ? resolveAttendanceRestrictedHome(data)
      : null,
  }
}
